import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Toolbar,
  AppBar,
  Avatar,
  Menu,
  MenuItem,
  Badge,
} from '@mui/material';
import {
  Dashboard,
  People,
  Security,
  Settings,
  Business,
  Engineering,
  Inventory,
  ShoppingCart,
  Assessment,
  Payment,
  Notifications,
  Email,
  Web,
  Palette,
  Analytics,
  Receipt,
  AccountBalance,
  LocalShipping,
  Build,
  Category,
  VpnKey,
  SystemUpdate,
  AdminPanelSettings,
  ExpandLess,
  ExpandMore,
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  AccountCircle,
  ExitToApp,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRbac } from '../../context/RbacContext';
import { useAuth } from '../../context/AuthContext';
import { SimpleFooter } from '../layout/SimpleFooter';
import { Topbar } from '../layout/Topbar';
import { getRoleName } from '../../utils/roleUtils';
import { SidebarLoader } from '../common/SidebarLoader';
import { BRAND } from '../../config/brand';
import {
  NAVY,
  NAVY_DARK,
  NAVY_RGB,
  getNavItemSx,
  getNavIconSx,
  getNavTextProps,
  getNavChildrenListSx,
} from '../../utils/portalNavStyles';

interface AdminSidebarProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const drawerWidth = 280;

const adminMenuItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/admin/dashboard',
    icon: <Dashboard />,
    permission: 'admin:dashboard',
  },
  {
    id: 'user-management',
    label: 'User Management',
    icon: <People />,
    permission: 'admin:users',
    children: [
      {
        id: 'users',
        label: 'Users',
        path: '/admin/users',
        icon: <People />,
        permission: 'admin:users',
      },
      {
        id: 'roles',
        label: 'Roles & Permissions',
        path: '/admin/roles',
        icon: <Security />,
        permission: 'admin:roles',
      },
    ],
  },
  {
    id: 'system-management',
    label: 'System Management',
    icon: <Settings />,
    permission: 'admin:system',
    children: [
      {
        id: 'system-settings',
        label: 'System Settings',
        path: '/admin/settings',
        icon: <SystemUpdate />,
        permission: 'admin:system',
      },
      {
        id: 'content-management',
        label: 'Content Management',
        path: '/admin/content',
        icon: <Web />,
        permission: 'admin:content',
      },
      {
        id: 'theme-management',
        label: 'Theme Management',
        path: '/admin/theme',
        icon: <Palette />,
        permission: 'admin:theme',
      },
      {
        id: 'email-management',
        label: 'Email Management',
        path: '/admin/email',
        icon: <Email />,
        permission: 'admin:email',
      },
      {
        id: 'notification-management',
        label: 'Notifications',
        path: '/admin/notifications',
        icon: <Notifications />,
        permission: 'admin:notifications',
      },
    ],
  },
  {
    id: 'production-operations',
    label: 'Production Operations',
    icon: <Engineering />,
    permission: 'admin:production',
    children: [
      {
        id: 'production-hub',
        label: 'Operations Hub',
        path: '/admin/production/operations',
        icon: <Engineering />,
        permission: 'admin:production',
      },
      {
        id: 'production-lines',
        label: 'Production Lines',
        path: '/admin/production',
        icon: <Engineering />,
        permission: 'admin:production',
      },
      {
        id: 'machine-floor',
        label: 'Machine Floor',
        path: '/admin/production/machines',
        icon: <Build />,
        permission: 'admin:production',
      },
      {
        id: 'production-queue',
        label: 'Production Queue',
        path: '/admin/production/queue',
        icon: <LocalShipping />,
        permission: 'admin:production',
      },
      {
        id: 'warehouse',
        label: 'Warehouse',
        path: '/admin/warehouse',
        icon: <Inventory />,
        permission: 'admin:inventory',
      },
      {
        id: 'deliveries',
        label: 'Deliveries',
        path: '/admin/deliveries',
        icon: <LocalShipping />,
        permission: 'admin:orders',
      },
      {
        id: 'financials',
        label: 'Financials',
        path: '/admin/financials',
        icon: <Receipt />,
        permission: 'admin:payments',
      },
    ],
  },
  {
    id: 'business-operations',
    label: 'Business Operations',
    icon: <Business />,
    permission: 'admin:business',
    children: [
      {
        id: 'inventory-management',
        label: 'Inventory Management',
        path: '/admin/inventory',
        icon: <Inventory />,
        permission: 'admin:inventory',
      },
      {
        id: 'order-management',
        label: 'Order Management',
        path: '/admin/orders',
        icon: <ShoppingCart />,
        permission: 'admin:orders',
      },
      {
        id: 'customer-management',
        label: 'Customer Management',
        path: '/admin/customers',
        icon: <People />,
        permission: 'admin:customers',
      },
      {
        id: 'product-management',
        label: 'Product Management',
        path: '/admin/products',
        icon: <Category />,
        permission: 'admin:products',
      },
      {
        id: 'quality-control',
        label: 'Quality Control',
        path: '/admin/quality',
        icon: <Assessment />,
        permission: 'admin:quality',
      },
      {
        id: 'maintenance-management',
        label: 'Maintenance Management',
        path: '/admin/maintenance',
        icon: <Build />,
        permission: 'admin:maintenance',
      },
      {
        id: 'supply-chain',
        label: 'Supply Chain',
        path: '/admin/supply-chain',
        icon: <LocalShipping />,
        permission: 'admin:supply-chain',
      },
    ],
  },
  {
    id: 'financial-management',
    label: 'Financial Management',
    icon: <Payment />,
    permission: 'admin:payments',
    children: [
      {
        id: 'payment-management',
        label: 'Payment Management',
        path: '/admin/payments',
        icon: <Payment />,
        permission: 'admin:payments',
      },
      {
        id: 'invoices',
        label: 'Invoices',
        path: '/admin/invoices',
        icon: <Receipt />,
        permission: 'admin:payments',
      },
      {
        id: 'bank-payments',
        label: 'Bank Payments',
        path: '/admin/bank-payments',
        icon: <AccountBalance />,
        permission: 'admin:payments',
      },
      {
        id: 'mpesa-integration',
        label: 'M-Pesa Integration',
        path: '/admin/mpesa',
        icon: <Payment />,
        permission: 'admin:payments',
      },
    ],
  },
  {
    id: 'analytics-reports',
    label: 'Analytics & Reports',
    icon: <Analytics />,
    permission: 'admin:analytics',
    children: [
      {
        id: 'analytics-dashboard',
        label: 'Analytics Dashboard',
        path: '/admin/analytics',
        icon: <Analytics />,
        permission: 'admin:analytics',
      },
      {
        id: 'reports',
        label: 'Reports',
        path: '/admin/reports',
        icon: <Assessment />,
        permission: 'admin:analytics',
      },
      {
        id: 'performance-metrics',
        label: 'Performance Metrics',
        path: '/admin/performance',
        icon: <Analytics />,
        permission: 'admin:analytics',
      },
    ],
  },
  {
    id: 'loader-demo',
    label: 'Loader Demo',
    path: '/admin/loader-demo',
    icon: <Analytics />,
    permission: 'admin:all',
  },
];

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ open, onClose, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole, user, loading } = useRbac();
  const isAdmin = hasRole('admin');
  const [expanded, setExpanded] = useState<string[]>([]);

  useEffect(() => {
    const activeParents = adminMenuItems
      .filter((item) => item.children?.some((child: { path?: string }) => child.path && location.pathname.startsWith(child.path)))
      .map((item) => item.id);
    if (activeParents.length > 0) {
      setExpanded((prev) => Array.from(new Set([...prev, ...activeParents])));
    }
  }, [location.pathname]);

  // Show skeleton loader while loading
  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <SidebarLoader />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { md: `calc(100% - 280px)` },
            ml: { md: '280px' },
            minHeight: '100vh',
            backgroundColor: theme.palette.background.default,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <Box sx={{ 
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            mt: '64px',
          }}>
            <Box sx={{ 
              flexGrow: 1,
              p: 3,
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Box sx={{ flexGrow: 1 }}>
                {children}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  const handleExpandClick = (id: string) => {
    setExpanded(prev =>
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const renderMenuItem = (item: any, level: number = 0) => {
    if (!isAdmin) {
      return null;
    }

    const indent = level > 0 ? 32 * level : 0;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expanded.includes(item.id);
    const isActive = item.path ? location.pathname === item.path || location.pathname.startsWith(`${item.path}/`) : false;

    if (hasChildren) {
      return (
        <Box key={item.id}>
          <ListItemButton
            onClick={() => handleExpandClick(item.id)}
            sx={getNavItemSx(level, isExpanded)}
          >
            <ListItemIcon sx={getNavIconSx(level)}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={getNavTextProps(level)}
            />
            {isExpanded ? <ExpandLess sx={{ color: NAVY }} /> : <ExpandMore sx={{ color: NAVY }} />}
          </ListItemButton>
          <Collapse in={isExpanded} timeout="auto">
            <List component="div" disablePadding sx={getNavChildrenListSx()}>
              {item.children.map((child: any) => renderMenuItem(child, level + 1))}
            </List>
          </Collapse>
        </Box>
      );
    }

    return (
      <ListItem key={item.id} disablePadding>
        <ListItemButton
          onClick={() => handleNavigation(item.path)}
          sx={getNavItemSx(level, isActive)}
          selected={isActive}
        >
          <ListItemIcon sx={getNavIconSx(level)}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            primaryTypographyProps={getNavTextProps(level)}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Brand Section */}
      <Box
        sx={{
          p: 3,
          background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
          color: 'white',
          textAlign: 'center',
        }}
      >
        <AdminPanelSettings sx={{ fontSize: 40, mb: 1 }} />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Admin Panel
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          Production Management System
        </Typography>
      </Box>

      {/* User Info */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar sx={{ mr: 2, bgcolor: NAVY }}>
            {user?.name?.[0] || 'A'}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {user?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getRoleName(user?.role as any)} • {user?.email}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Menu Items */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 2 }}>
        <List>
          {adminMenuItems.map((item) => renderMenuItem(item))}
        </List>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          Admin Portal v1.0
        </Typography>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          © 2024 Monio Production
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={open}
          onClose={onClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: theme.palette.background.paper,
              zIndex: theme.zIndex.drawer,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            backgroundColor: theme.palette.background.paper,
            borderRight: 1,
            borderColor: 'divider',
            zIndex: theme.zIndex.drawer,
            position: 'fixed',
            height: '100vh',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {/* Top Bar */}
        <Topbar onDrawerToggle={onClose} notificationsCount={4} />

        {/* Page Content */}
        <Box sx={{ 
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          mt: '64px', // Add top margin to account for fixed topbar
        }}>
          <Box sx={{ 
            flexGrow: 1,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Box sx={{ flexGrow: 1 }}>
              {children}
            </Box>
            <SimpleFooter />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}; 